import moment from 'moment'
import { observer } from 'mobx-react-lite'
import Button from 'app/components/Button'
import { useConfirmation } from 'app/components/ConfirmationContext'
import { useFeatureToggle } from 'app/hooks/useFeatureToggle'
import { closeModal } from 'app/lib/modal'
import { FACEBOOK_API_VERSION } from 'app/services/facebook.constants'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { useUser } from 'app/utils'
import { isSMCOrg } from 'lib/isSMCOrg'

// observer: reads a feature toggle (and possibly authStore.user) in render; useFeatureToggle resolves async and its contract requires an observer wrapper (TRK-6020).
export const FacebookWorkspaceModalForm = observer(function FacebookWorkspaceModalForm({
  onIntegrationChanged,
  integrationType,
  currentValues,
  onBeforeConnect,
  onBack,
}: {
  onIntegrationChanged: () => void
  integrationType: `facebook-ads` | `facebook-leads`
  currentValues?: {
    tokenSet?: boolean
    lastUpdated?: string
  }
  /** Wizard hook: called right before the OAuth redirect (used to set the resume flag). */
  onBeforeConnect?: () => void
  /** Wizard back target: when set, create mode renders body copy plus a footer with Back and the Facebook CTA. */
  onBack?: () => void
}) {
  const user = useUser()
  const [confirm] = useConfirmation()
  const usePersonalToken = useFeatureToggle(`facebook-leads-personal-token`)

  if (!user) return <></>

  const handleConnectClick = () => {
    onBeforeConnect?.()
    const state = `${user.workspaceId}|${integrationType}`
    if ((usePersonalToken || isSMCOrg(user.orgId)) && integrationType === `facebook-leads`) {
      const scopes = [
        `ads_read`,
        `ads_management`,
        `business_management`,
        `pages_show_list`,
        `pages_read_engagement`,
        `pages_manage_ads`,
        `leads_retrieval`,
        `pages_manage_metadata`,
      ].join(`,`)
      window.location.href = `https://www.facebook.com/${FACEBOOK_API_VERSION}/dialog/oauth?client_id=2254306408261695&redirect_uri=${
        window.location.origin + `/auth/facebook/callback`
      }&state=${state}&scope=${scopes}&response_type=code`
    } else {
      const leadsConfigId = `1296838888637699`
      const adsConfigId = `1747994819428441`
      const configId = integrationType === `facebook-ads` ? adsConfigId : leadsConfigId

      window.location.href = `https://www.facebook.com/${FACEBOOK_API_VERSION}/dialog/oauth?client_id=2254306408261695&redirect_uri=${
        window.location.origin + `/auth/facebook/callback`
      }&state=${state}&config_id=${configId}&response_type=code`
    }
  }

  const handleRemoveClick = async () => {
    confirm({
      title: `Remove Facebook Workspace Integration?`,
      description: `Removing this workspace integration will also remove all associated data. Do you want to continue?`,
      variant: `danger`,
    })
      .then(() => {
        window
          .fetch(`/api/integrations/setup/${integrationType}`, {
            method: `DELETE`,
            credentials: `include`,
            headers: { 'Content-Type': `application/json` },
            body: JSON.stringify({}),
          })
          .then((r) => r.json())
          .then((res) => {
            if (res?.success) {
              notifySuccess(`Facebook workspace integration removed`)
              onIntegrationChanged()
              closeModal()
            } else {
              notifyError(`Failed to remove: ${res?.message}`)
            }
          })
          .catch((error) => {
            notifyError(`Failed to remove integration: ${error}`)
          })
      })
      .catch(() => {
        // User cancelled the confirmation
      })
  }

  return (
    <>
      {currentValues?.tokenSet ? (
        <div className="flex w-[600px] flex-col gap-4 p-6">
          <p className="text-body-md">Your Facebook account is linked to Trackable</p>
          <p className="mb-6 text-body-md">Last Update: {moment(currentValues.lastUpdated).format(`MM/DD/YYYY`)}</p>
          <Button mode="primary" label="Reconnect Facebook" onClick={handleConnectClick} />
          <Button mode="danger" label="Remove" onClick={handleRemoveClick} />
        </div>
      ) : onBack ? (
        <>
          <div className="w-[600px] px-6 py-4">
            <p className="text-body-md text-default">
              Connect your Facebook account so Trackable can access your{` `}
              {integrationType === `facebook-ads` ? `ad accounts` : `lead forms`}. You&apos;ll be redirected to Facebook
              to approve access.
            </p>
          </div>
          <div className="sticky bottom-0 w-full rounded-b-xl border-t border-default bg-surface-raised px-6 py-3">
            <div className="flex items-center justify-end gap-2">
              <Button label="Back" mode="white" size="md" onClick={onBack} />
              <Button mode="primary" size="md" label="Continue with Facebook" onClick={handleConnectClick} />
            </div>
          </div>
        </>
      ) : (
        <div className="flex h-[160px] w-[500px] items-center justify-center">
          <Button
            mode="primary"
            label={`Login with Facebook for ${integrationType === `facebook-ads` ? `Ads` : `Leads`}`}
            onClick={handleConnectClick}
          />
        </div>
      )}
    </>
  )
})
